import React, { useState } from 'react';
import { max, min } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';
// @ts-ignore
import StarRatings from 'react-star-ratings';
import { useSpring, animated } from '@react-spring/web';
import { GiAnticlockwiseRotation } from 'react-icons/gi';

import { Svg, Group, Background, AxisBottom, AxisGrid, AxisRight, Label, Hint, Tooltip } from './partials';

import { useDimensions } from '../utils/useDimensions';
import { DragMove } from '../utils/DragMove';
import { formatter } from '../utils/formatter';
import { firstYear, lastYear, middleYear, firstValue, lastValue } from '../utils/accessor';
import { adaptLabels } from '../utils/adaptLabels';
import { lineSimilarity } from '../utils/lineSimilarity';
import { quantifyScore } from '../utils/quantifyScore';

import { LegendWrapper, StarWrapper, Button } from '../style.DrawIt';
import { IChart, ILine, ILabel, ITooltip } from '../types.DrawIt';

import { elections, coalitions, eras } from '../config/labels';
import { chartColors, appColors } from '../config/colors';

export const Chart: React.FC<IChart> = ({
    id,
    data,
    annotations,
    unit = '',
    factor = 1,
    precision = 1,
    minValue,
    maxValue,
    breakpoint = data[0].year,
    width = 640,
    height = 380,
    marginRight,
    showHint,
    isComplete,
    setIsComplete,
    totalScore,
    setTotalScore
}) => {
    const [isDrawing, setIsDrawing] = useState<boolean>(false);
    const [userStars, setUserStars] = useState<number>(0);
    const [tooltip, setTooltip] = useState<ITooltip>({
        isOpen: false,
        label: undefined,
        width: 95,
        height: 45,
        x: 0,
        y: 0
    });

    const { width: deviceWidth } = useDimensions();
    const isMobile = deviceWidth < 768;

    const previousData = data.filter(d => d.year <= breakpoint);
    const actualData = data.filter(d => d.year >= breakpoint);
    const pretty = formatter(unit, precision, factor);

    const margin = {
        top: isMobile ? 20 : 40,
        right: isMobile ? (marginRight ? marginRight * 0.8 : 50) : marginRight ? marginRight : 70,
        bottom: isMobile ? 50 : 55,
        left: isMobile ? 20 : 35
    };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const xMin = min(data, d => d.year)!;
    const xMax = max(data, d => d.year)!;
    const xScale = scaleLinear()
        .domain([xMin, xMax])
        .range([0, innerWidth]);

    const yMin = minValue || 0;
    // const yMin = minValue || min(values, (d: Line) => d.value)!;
    const yMax = maxValue || max(data, d => d.value)!;
    const yScale = scaleLinear()
        .domain([yMin, yMax])
        .range([innerHeight, 0]);

    const lineHelper = line<ILine>()
        .defined(d => !!d.value)
        .x(d => xScale(d.year))
        .y(d => yScale(d.value));

    const [userData, setUserData] = useState<ILine[]>([
        {
            year: lastYear(previousData),
            value: lastValue(previousData)
        }
    ]);

    const firstTranslate = (data: ILine[], offset: number = 0) => {
        if (lastValue(data) > firstValue(data)) offset = 40;
        return `translate(${xScale(firstYear(data))}, ${yScale(firstValue(data)) + offset})`;
    };

    const lastTranslate = (data: ILine[], offset: number = 0) => {
        if (lastValue(data) < firstValue(data)) offset = 40;
        return `translate(${xScale(lastYear(data))}, ${yScale(lastValue(data)) + offset})`;
    };

    const smartTranslate = (actualData: ILine[], userData: ILine[], offset: number = 0) => {
        if (lastValue(userData) < lastValue(actualData)) offset = 40;
        return `translate(${xScale(lastYear(userData))}, ${yScale(lastValue(userData)) + offset})`;
    };

    const lineAnimation = useSpring({
        config: {
            duration: isComplete ? 500 : 0
        },
        width: isComplete ? innerWidth + 5 : xScale(breakpoint)
    });

    const pulseAnimation = useSpring({
        config: {
            duration: 2000
        },
        loop: true,
        from: { r: 0, fillOpacity: 1 },
        to: { r: 15, fillOpacity: 0 }
    });

    const handleDragMove = (e: any) => {
        let newUserData = userData.slice();

        const rect = e.target.getBoundingClientRect();
        const x = e.clientX - rect.left - margin.left;
        const y = e.clientY - rect.top - margin.top;
        const year = Math.round(Math.max(breakpoint + 1, Math.min(xMax, xScale.invert(x))));
        const value = Math.max(yMin, Math.min(yMax, yScale.invert(y)));

        if (userData.some(d => d.year === year)) {
            newUserData = newUserData.map(d => (d.year === year ? { year, value } : d));
        } else {
            newUserData.push({ year, value });
        }

        if (!isComplete) {
            newUserData = newUserData.filter(d => d.year <= year);
            setUserData(newUserData);
        }

        if (!isDrawing) {
            setIsDrawing(true);
        }

        if (!isComplete && year === lastYear(actualData)) {
            const score = lineSimilarity(actualData, newUserData, yMax);
            const stars = quantifyScore(score);
            const newTotalScore = totalScore.slice(0);
            const oldScoreIndex = newTotalScore.findIndex(score => score.id === id);

            if (oldScoreIndex > -1) {
                newTotalScore[oldScoreIndex] = { id, score };
            } else {
                newTotalScore.push({ id, score });
            }

            setTotalScore(newTotalScore);
            setUserStars(stars);
            setIsComplete(true);
        }
    };

    const handleComplete = () => {
        setIsComplete(true);
    };

    const handleReset = () => {
        setIsDrawing(false);
        setIsComplete(false);
        setUserStars(0);
        setUserData([
            {
                year: lastYear(previousData),
                value: lastValue(previousData)
            }
        ]);
    };

    const openTooltip = (newLabel: ILabel) => {
        setTooltip(({ isOpen, x, y, label, ...rest }) => ({
            isOpen: true,
            label: newLabel,
            x: xScale(middleYear(newLabel.values as ILine[])) - tooltip.width / 2 + margin.left,
            y: yScale(yMin) - tooltip.height + margin.top - 10,
            ...rest
        }));
    };

    const closeTooltip = () => {
        setTooltip(({ isOpen, ...rest }) => ({ isOpen: false, ...rest }));
    };

    return (
        <>
            <Svg id={id} width={width} height={height} isTouchable={!isComplete} style={{ pointerEvents: 'none' }}>
                <defs>
                    <clipPath id={`clip-${id}`}>
                        <animated.rect {...lineAnimation} height={innerHeight} />
                    </clipPath>
                </defs>
                <DragMove onDragMove={handleDragMove}>
                    <rect
                        width={width}
                        height={height}
                        style={{ pointerEvents: 'all', cursor: 'pointer', opacity: 0 }}
                    />
                </DragMove>
                <Background width={width} height={height} />
                <AxisRight
                    scale={yScale}
                    ticks={yScale.ticks(6)}
                    tickFormatter={(d: any) => `${pretty(d)}`}
                    showTickMarks={false}
                    showLine={false}
                    scalingFactor={isMobile ? 0.8 : 1}
                    transform={`translate(${margin.left + innerWidth + 5}, ${margin.top})`}
                />
                <AxisGrid
                    scale={yScale}
                    ticks={yScale.ticks(6)}
                    tickMarkLength={innerWidth}
                    showTicks={false}
                    stroke={chartColors.lineSecondary}
                    scalingFactor={isMobile ? 0.8 : 1}
                    transform={`translate(${margin.left}, ${margin.top})`}
                />
                <AxisBottom
                    scale={xScale}
                    // ticks={xScale.ticks()}
                    ticks={elections.filter(year => year >= xMin && year <= xMax)}
                    showTickMarks={true}
                    showLine={false}
                    verticalLabels={isMobile}
                    scalingFactor={isMobile ? 0.8 : 1}
                    transform={`translate(${margin.left}, ${margin.top + innerHeight})`}
                />
                <AxisBottom
                    scale={xScale}
                    ticks={[xMin, xMax].filter(year => !elections.includes(year))}
                    showTickMarks={true}
                    showLine={false}
                    fill={chartColors.fontSecondary}
                    verticalLabels={isMobile}
                    scalingFactor={isMobile ? 0.8 : 1}
                    transform={`translate(${margin.left}, ${margin.top + innerHeight})`}
                />
                <Group
                    className="label-coalitions"
                    transform={`translate(${margin.left}, ${margin.top + innerHeight + 2})`}
                >
                    {adaptLabels(coalitions, xMin, xMax).map((label: ILabel, index) => (
                        <Group
                            key={index}
                            transform={`translate(${xScale(middleYear(label.values as ILine[])) - 5}, 13)`}
                            onMouseEnter={() => {
                                openTooltip(label);
                            }}
                            onMouseLeave={closeTooltip}
                            style={{ pointerEvents: 'all', cursor: 'pointer' }}
                        >
                            {label.values.map((value: any, index) => (
                                <circle
                                    key={index}
                                    cx={index * (isMobile ? 7 : 10)}
                                    r={isMobile ? 5 : 7}
                                    fill={label.colors[index]}
                                />
                            ))}
                        </Group>
                    ))}
                </Group>
                <Group className="label-eras" transform={`translate(${margin.left}, ${margin.top + innerHeight - 30})`}>
                    {adaptLabels(eras, xMin, xMax).map((label: ILabel, index) => (
                        <Group key={index} transform={`translate(${xScale(middleYear(label.values as ILine[]))}, 15)`}>
                            <text fontSize={isMobile ? '12' : '14'} fill={label.colors[0]} textAnchor="middle">
                                {label.text}
                            </text>
                        </Group>
                    ))}
                </Group>
                <Group className="user-values" transform={`translate(${margin.left}, ${margin.top})`}>
                    <path
                        d={lineHelper(userData)!}
                        fill="none"
                        stroke={chartColors.lineUser}
                        strokeWidth="4"
                        strokeDasharray="1 7"
                        strokeLinecap="round"
                    />
                    {isDrawing && !isComplete && (
                        <animated.circle
                            {...pulseAnimation}
                            cx={xScale(lastYear(userData))}
                            cy={yScale(lastValue(userData))}
                            fill={chartColors.lineUser}
                        />
                    )}
                    {isDrawing && (
                        <circle
                            cx={xScale(lastYear(userData))}
                            cy={yScale(lastValue(userData))}
                            r="5"
                            fill={chartColors.lineUser}
                        />
                    )}
                    {isDrawing && (
                        <Label
                            text={pretty(lastValue(userData))}
                            textAnchor="end"
                            fill={chartColors.lineUser}
                            hasOutline={true}
                            transform={smartTranslate(actualData, userData)}
                        />
                    )}
                </Group>
                {isComplete && (
                    <Group
                        className="actual-values"
                        clipPath={`url(#clip-${id})`}
                        transform={`translate(${margin.left}, ${margin.top})`}
                    >
                        <path
                            d={lineHelper(actualData)!}
                            fill="none"
                            stroke={chartColors.lineActual}
                            strokeWidth="4"
                            strokeLinecap="round"
                        />
                        {actualData.map((d, index) => (
                            <circle
                                key={index}
                                cx={xScale(d.year)}
                                cy={yScale(d.value)}
                                r="5"
                                fill={chartColors.lineActual}
                                visibility={!!d.value ? 'visible' : 'hidden'}
                            />
                        ))}
                        <Label
                            text={pretty(lastValue(actualData))}
                            textAnchor="end"
                            fill={chartColors.lineActual}
                            hasOutline={true}
                            transform={lastTranslate(actualData)}
                        />
                    </Group>
                )}
                <Group className="previous-values" transform={`translate(${margin.left}, ${margin.top})`}>
                    <path
                        d={lineHelper(previousData)!}
                        fill="none"
                        stroke={chartColors.linePrevious}
                        strokeWidth="4"
                        strokeLinecap="round"
                    />
                    {!isDrawing && !isComplete && (
                        <animated.circle
                            {...pulseAnimation}
                            cx={xScale(lastYear(previousData))}
                            cy={yScale(lastValue(previousData))}
                            fill={chartColors.linePrevious}
                        />
                    )}
                    {previousData.map((d, index) => (
                        <circle
                            key={index}
                            cx={xScale(d.year)}
                            cy={yScale(d.value)}
                            r="5"
                            fill={chartColors.linePrevious}
                            visibility={!!d.value ? 'visible' : 'hidden'}
                        />
                    ))}
                    <Label
                        text={pretty(firstValue(previousData))}
                        textAnchor="start"
                        fill={chartColors.linePrevious}
                        hasOutline={true}
                        transform={firstTranslate(previousData)}
                    />
                    <Label
                        text={pretty(lastValue(previousData))}
                        textAnchor="middle"
                        fill={chartColors.linePrevious}
                        hasOutline={true}
                        transform={lastTranslate(previousData)}
                    />
                </Group>
                <Group
                    className="label-annotations"
                    clipPath={isComplete ? '' : `url(#clip-${id})`}
                    transform={`translate(${margin.left}, ${margin.top})`}
                >
                    {isComplete &&
                        !!annotations &&
                        annotations.map((annotation, index) => (
                            <Group
                                key={index}
                                transform={`translate(${xScale(annotation.year)}, ${yScale(annotation.value) - 50})`}
                            >
                                <text
                                    fill="none"
                                    stroke={chartColors.backgroundPrimary}
                                    strokeWidth="0.25rem"
                                    textAnchor="middle"
                                >
                                    {annotation.text}
                                </text>
                                <text fill={chartColors.fontPrimary} textAnchor="middle">
                                    {annotation.text}
                                </text>
                                <line
                                    stroke={chartColors.linePrimary}
                                    strokeWidth="2"
                                    x1="0"
                                    x2="0"
                                    y1="10"
                                    y2="35"
                                ></line>
                            </Group>
                        ))}
                </Group>
                <Tooltip tooltip={tooltip} />
                {showHint && !isDrawing && !isComplete && (
                    <Group className="user-hint" transform={`translate(${margin.left + 20}, ${margin.top + 5})`}>
                        <Hint text="Zeichen Sie die Line zu Ende" transform={lastTranslate(previousData)}></Hint>
                    </Group>
                )}
            </Svg>

            <LegendWrapper>
                <Button onClick={handleComplete}>Ergebnis anzeigen</Button>
                <Button onClick={handleReset}>
                    <GiAnticlockwiseRotation />
                </Button>
                <StarWrapper alignRight={true}>
                    <StarRatings
                        rating={userStars}
                        starRatedColor={appColors.starBackground}
                        starEmptyColor={appColors.emptyStarBackground}
                        starDimension={isMobile ? '1.5rem' : '2rem'}
                        starSpacing="0"
                    />
                </StarWrapper>
            </LegendWrapper>
        </>
    );
};
